/**
 * EditableField Components
 *
 * Shared components for inline editing of text and select values.
 * Consolidates patterns from analysis/ and meeting-script/ components.
 *
 * @example
 * // Inline text editing (blur to save)
 * <EditableText value={name} onChange={setName} />
 *
 * // Text with explicit save/cancel buttons
 * <EditableTextWithButtons value={notes} onChange={setNotes} multiline />
 *
 * // Select dropdown
 * <EditableSelect
 *   value={status}
 *   options={[{ value: 'active', label: 'Active' }]}
 *   onChange={setStatus}
 * />
 *
 * // Deletable wrapper
 * <DeletableItem onDelete={handleDelete}>
 *   <TaskCard task={task} />
 * </DeletableItem>
 */

import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Edit3, Save, X, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  className?: string;
  placeholder?: string;
  maxLength?: number;
}

interface EditableTextWithButtonsProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  multiline?: boolean;
  placeholder?: string;
}

interface SelectOption {
  value: string;
  label: string;
  className?: string;
  color?: string;
}

interface EditableSelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  className?: string;
  displayClassName?: string;
}

interface DeletableItemProps {
  onDelete: () => void;
  children: React.ReactNode;
  className?: string;
}

// =============================================================================
// EDITABLE TEXT (Inline mode - blur to save)
// =============================================================================

export function EditableText({
  value,
  onChange,
  multiline = false,
  className,
  placeholder = 'Click to edit...',
  maxLength,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue.trim() !== value) {
      onChange(editValue.trim());
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Enter' && multiline && e.ctrlKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    const commonProps = {
      ref: inputRef as React.RefObject<HTMLInputElement & HTMLTextAreaElement>,
      value: editValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setEditValue(e.target.value),
      onBlur: handleSave,
      onKeyDown: handleKeyDown,
      placeholder,
      className: cn(
        'w-full rounded border border-primary/50 bg-background px-1 py-0.5 text-xs',
        'focus:outline-none focus:ring-1 focus:ring-primary/30',
        className
      ),
    };

    return multiline ? (
      <textarea {...commonProps} rows={3} className={cn(commonProps.className, 'text-sm')} />
    ) : (
      <input type="text" {...commonProps} />
    );
  }

  const shouldTruncate = maxLength && value && value.length > maxLength && !isExpanded;
  const displayValue = shouldTruncate ? value.slice(0, maxLength) + '...' : value;

  return (
    <span className={cn('inline', className)}>
      <span
        onClick={() => setIsEditing(true)}
        className={cn(
          'cursor-pointer transition-colors text-xs',
          'hover:opacity-70',
          !value && 'text-muted-foreground italic'
        )}
        title="Click to edit"
      >
        {displayValue || placeholder}
      </span>
      {shouldTruncate && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
          }}
          className="ml-1 text-xs text-primary hover:underline"
        >
          See more
        </button>
      )}
      {maxLength && value && value.length > maxLength && isExpanded && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(false);
          }}
          className="ml-1 text-xs text-primary hover:underline"
        >
          See less
        </button>
      )}
    </span>
  );
}

// =============================================================================
// EDITABLE TEXT WITH BUTTONS (Explicit save/cancel)
// =============================================================================

export function EditableTextWithButtons({
  value,
  onChange,
  className,
  multiline = false,
  placeholder,
}: EditableTextWithButtonsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onChange(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex gap-2 items-start">
        {multiline ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[80px] flex-1"
            autoFocus
          />
        ) : (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
            autoFocus
          />
        )}
        <Button size="sm" variant="ghost" onClick={handleSave}>
          <Save className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'cursor-pointer hover:bg-accent/30 rounded px-1 -mx-1 transition-colors group',
        className
      )}
      onClick={() => setIsEditing(true)}
      title="Click to edit"
    >
      {value || (
        <span className="text-muted-foreground italic">
          {placeholder || 'Click to add text...'}
        </span>
      )}
      <Edit3 className="h-3 w-3 inline ml-1 opacity-0 group-hover:opacity-50 transition-opacity" />
    </div>
  );
}

// =============================================================================
// EDITABLE SELECT (Dropdown)
// =============================================================================

export function EditableSelect({
  value,
  options,
  onChange,
  className,
  displayClassName,
}: EditableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label || value;
  const displayClass = selectedOption?.className;
  const displayColor = selectedOption?.color;

  const nonColorClasses = displayClassName
    ?.split(' ')
    .filter((c) => !c.startsWith('text-') || c === 'text-xs' || c === 'text-sm' || c === 'text-base')
    .join(' ');

  return (
    <div ref={containerRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'text-xs font-medium transition-colors cursor-pointer',
          'hover:opacity-70',
          displayColor ? nonColorClasses : displayClass || displayClassName
        )}
        style={displayColor ? { color: displayColor } : undefined}
      >
        {displayLabel}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[100px] rounded-md border bg-popover p-1 shadow-lg">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={cn(
                'w-full rounded-sm px-2 py-1 text-left text-xs transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                value === option.value && 'bg-accent/50'
              )}
            >
              <span
                className={!option.color ? option.className : undefined}
                style={option.color ? { color: option.color } : undefined}
              >
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// DELETABLE ITEM (Wrapper with delete button)
// =============================================================================

export function DeletableItem({ onDelete, children, className }: DeletableItemProps) {
  return (
    <div className={cn('group relative', className)}>
      {children}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="absolute -right-2 -top-2 p-1 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80"
        title="Delete item"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}
